import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../utility/apiFetch.js";

// Grow-level sensor assignment: GET/PUT /api/v1/grows/{id}/sensors.
// Response/body shape: { sensor_mode: "device_wide" | "custom", sensor_ids: number[] }.
export function useGrowSensorsQuery(growId) {
  return useQuery({
    queryKey: ["grow-sensors", growId],
    queryFn: () => apiFetch(`grows/${growId}/sensors`),
    enabled: !!growId,
    staleTime: 60_000,
  });
}

export function useSetGrowSensors(growId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch(`grows/${growId}/sensors`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grow-sensors", growId] });
      qc.invalidateQueries({ queryKey: ["grow", growId] });
    },
  });
}

// Phase-level sensor assignment: GET/PUT /api/v1/grows/{growId}/phases/{phaseId}/sensors.
// Same {sensor_mode, sensor_ids} shape as the grow-level endpoint.
export function useGrowPhaseSensorsQuery(growId, phaseId) {
  return useQuery({
    queryKey: ["grow-phase-sensors", growId, phaseId],
    queryFn: () => apiFetch(`grows/${growId}/phases/${phaseId}/sensors`),
    enabled: !!growId && !!phaseId,
    staleTime: 60_000,
  });
}

export function useSetGrowPhaseSensors(growId, phaseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch(`grows/${growId}/phases/${phaseId}/sensors`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grow-phase-sensors", growId, phaseId] });
      qc.invalidateQueries({ queryKey: ["grow-phases", growId] });
    },
  });
}

// Inventory-item-level sensor assignment: GET/PUT /api/v1/inventory/{id}/sensors.
// Same {sensor_mode, sensor_ids} shape as the grow-level endpoint. Inventory items have
// no phases, so this is the only level for them (item -> device-wide), no fallback chain.
export function useInventoryItemSensorsQuery(itemId) {
  return useQuery({
    queryKey: ["inventory-sensors", itemId],
    queryFn: () => apiFetch(`inventory/${itemId}/sensors`),
    enabled: !!itemId,
    staleTime: 60_000,
  });
}

export function useSetInventoryItemSensors(itemId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch(`inventory/${itemId}/sensors`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory-sensors", itemId] });
      qc.invalidateQueries({ queryKey: ["inventory", itemId] });
    },
  });
}
